export const MIN_FONT_PX = 8;
export const MAX_FONT_PX = 28;
export const FONT_AREA_RATIO = 0.34;
export const FIXED_BASE_PX = 15;
export const MIN_RENDER_FONT_PX = 6;

const SPRITE_REF_PX_PER_UNIT = 8;

function computeBaseLabelFontPx({
  adaptive,
  fontScale = 1,
  area = 4,
  pxPerUnit,
}) {
  const minFont = Math.max(6, MIN_FONT_PX * fontScale);
  const maxFont = Math.max(minFont + 1, MAX_FONT_PX * fontScale);

  if (!adaptive) {
    return FIXED_BASE_PX * fontScale;
  }

  const raw =
    Math.sqrt(Math.max(area, 0.25)) * pxPerUnit * FONT_AREA_RATIO * fontScale;
  return Math.max(minFont, Math.min(maxFont, raw));
}

export function computeCanvasLabelFontPx({
  adaptive,
  sizePercent,
  fontScale = 1,
  area = 4,
  pxPerUnit,
}) {
  const scale = Math.max(0.01, (sizePercent ?? 100) / 100);
  const baseSize = computeBaseLabelFontPx({
    adaptive,
    fontScale,
    area,
    pxPerUnit,
  });
  return Math.max(1, baseSize * scale);
}

export function computeSpriteLabelScale({
  adaptive,
  sizePercent,
  fontScale = 1,
  area = 4,
}) {
  const scale = Math.max(0.01, (sizePercent ?? 100) / 100);
  const baseSize = computeBaseLabelFontPx({
    adaptive,
    fontScale,
    area,
    pxPerUnit: SPRITE_REF_PX_PER_UNIT,
  });
  return (baseSize * scale) / SPRITE_REF_PX_PER_UNIT;
}

function forEachTriangle(polygon, visit) {
  if (!Array.isArray(polygon) || polygon.length < 9) return;
  for (let i = 0; i + 8 < polygon.length; i += 9) {
    visit(
      polygon[i],
      polygon[i + 1],
      polygon[i + 2],
      polygon[i + 3],
      polygon[i + 4],
      polygon[i + 5],
      polygon[i + 6],
      polygon[i + 7],
      polygon[i + 8],
    );
  }
}

function triangleAreaAndNormal(ax, ay, az, bx, by, bz, cx, cy, cz) {
  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const acx = cx - ax;
  const acy = cy - ay;
  const acz = cz - az;
  const nx = aby * acz - abz * acy;
  const ny = abz * acx - abx * acz;
  const nz = abx * acy - aby * acx;
  return {
    area: 0.5 * Math.hypot(nx, ny, nz),
    nx,
    ny,
    nz,
  };
}

export function getTriangulatedFaceArea(polygon) {
  let area = 0;
  forEachTriangle(polygon, (ax, ay, az, bx, by, bz, cx, cy, cz) => {
    area += triangleAreaAndNormal(ax, ay, az, bx, by, bz, cx, cy, cz).area;
  });
  return area > 0 ? area : 4;
}

export function getTriangulatedFaceCentroid(polygon) {
  let totalArea = 0;
  let cx = 0;
  let cy = 0;
  let cz = 0;
  let count = 0;

  forEachTriangle(polygon, (ax, ay, az, bx, by, bz, cx0, cy0, cz0) => {
    const { area } = triangleAreaAndNormal(
      ax,
      ay,
      az,
      bx,
      by,
      bz,
      cx0,
      cy0,
      cz0,
    );
    const mx = (ax + bx + cx0) / 3;
    const my = (ay + by + cy0) / 3;
    const mz = (az + bz + cz0) / 3;
    if (area > 1e-12) {
      cx += mx * area;
      cy += my * area;
      cz += mz * area;
      totalArea += area;
    } else {
      cx += mx;
      cy += my;
      cz += mz;
      count += 1;
    }
  });

  if (totalArea > 1e-12) {
    return [cx / totalArea, cy / totalArea, cz / totalArea];
  }
  if (count > 0) {
    return [cx / count, cy / count, cz / count];
  }
  return null;
}
